import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { rndData } from "./RnDData";
import DetectionAnimation from "../capabilities/DetectionAnimation";
import "../components/StandardPage.jsx";
import "../../css/components/standardPage.css";

const RnDSection = ({ id, data, sectionName, showDetectionAnimation }) => {
  const scrollToNext = () => {
    const sections = ["moving", "processing"];
    const currentIndex = sections.indexOf(id);
    if (currentIndex < sections.length - 1) {
      const nextSection = document.getElementById(sections[currentIndex + 1]);
      const navbarElement = document.getElementsByClassName("header")[0];
      if (nextSection && navbarElement) {
        const sectionTop = nextSection.offsetTop;
        const navbarHeight = navbarElement.offsetHeight;
        window.scrollTo({
          top: sectionTop - navbarHeight,
          behavior: "smooth",
        });
      }
    }
  };

  const isVideo = (url) => {
    if (!url) return false;
    return url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov');
  };

  return (
    <div id={id} className="capability-section-wrapper">
      {/* Title Banner - optional, appears above hero section */}
      {data.titleAbove && (
        <div className="title-banner">
          <div className="title-banner-particles">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="particle"></div>
            ))}
          </div>
          <h1 className="page-title">
            <span className="section-prefix">{sectionName}</span>
          </h1>
        </div>
      )}
      
      {/* Hero Section with background - contains header and smaller cards */}
      <div
        className={`page-hero ${data.cardsPosition === 'bottom' ? 'cards-bottom' : ''} ${data.content[0]?.tiltShift ? 'tilt-shift-section' : ''}`}
        style={{
          backgroundImage: !isVideo(data.content[0]?.href) && data.content[0]?.href ? `url(${data.content[0].href})` : undefined,
        }}
      >
        {isVideo(data.content[0]?.href) && (
          <video
            autoPlay
            loop
            muted
            playsInline
            poster={data.content[0]?.poster}
            className="background-video"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
            }}
          >
            <source src={data.content[0].href} type="video/mp4" />
          </video>
        )}
        <div className="hero-overlay"></div>
        
        {/* Detection animation for Detect section only */}
        {showDetectionAnimation && <DetectionAnimation />}
        
        {/* Title - show if titleAbove is true (moves inside hero section) or if titleAbove is false (original behavior) */}
        {data.titleAbove ? (
          <h1 className="page-title" style={{ marginBottom: '2rem' }}>
            {data.title}
          </h1>
        ) : (
        <h1 className="page-title">
          <span className="section-prefix">{sectionName}:</span> {data.title}
        </h1>
        )}
        
        {/* Hero Cards - smaller cards with key points */}
        <div className="hero-cards-container">
          {data.heroCards.map((card, index) => (
            <div key={index} className="hero-card">
              <h3 className="hero-card-title">{card.title}</h3>
              <p className="hero-card-description">{card.description}</p>
            </div>
          ))}
        </div>
        
        {/* <button
          className="scroll-arrow"
          onClick={scrollToNext}
          aria-label="Scroll to next section"
        >
          <ChevronDown className="arrow-icon" size={32} strokeWidth={3} />
        </button> */}
      </div>

      {/* Content Sections */}
      <div className="content-sections">
        {data.content.slice(1).map((section, index) => (
          <div
            key={index}
            className={`content-section ${section.cardsPosition === 'bottom' ? 'cards-bottom' : ''} ${section.tiltShift ? 'tilt-shift-section' : ''}`}
            style={{
              backgroundImage: !isVideo(section.href) && section.href ? `url(${section.href})` : undefined,
            }}
          >
            {isVideo(section.href) && (
              <video
                autoPlay
                loop
                muted
                playsInline
                poster={section.poster}
                className="background-video"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  zIndex: 0,
                }}
              >
                <source src={section.href} type="video/mp4" />
              </video>
            )}
            <div className="section-overlay"></div>
            {section.title && <h2 className="section-title-overlay">{section.title}</h2>}
            <div className={`section-cards-container ${section.cards.length === 2 ? 'two-cards' : section.cards.length === 3 ? 'three-cards' : ''}`}>
              {section.cards.map((card, cardIndex) => (
                <div key={cardIndex} className="section-card">
                  <p className="section-card-description">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RnDPage = () => {
  const location = useLocation();
  const [scrolledToHash, setScrolledToHash] = useState(false);

  useEffect(() => {
    // Scroll to hash on initial load or when hash changes
    if (location.hash && !scrolledToHash) {
      setTimeout(() => {
        const id = location.hash.replace("#", "");
        const element = document.getElementById(id);
        const navbarElement = document.getElementsByClassName("header")[0];
        
        if (element && navbarElement) {
          const elementTop = element.offsetTop;
          const navbarHeight = navbarElement.offsetHeight;
          window.scrollTo({
            top: elementTop - navbarHeight,
            behavior: "smooth",
          });
          setScrolledToHash(true);
        }
      }, 100);
    }
  }, [location.hash, scrolledToHash]);

  // Reset scrolledToHash when hash changes
  useEffect(() => {
    setScrolledToHash(false);
  }, [location.hash]);

  return (
    <div className="standard-page">
      <RnDSection id="moving" data={rndData.moving} sectionName="Move" />
      <RnDSection id="processing" data={rndData.processing} sectionName="Process" />
    </div>
  );
};

export default RnDPage;

